import { AllReportYears, getAllReports } from 'npo/pages/ReportsBeta/api';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import styled from 'styled-components';
import ReportSharingProgram from './ReportSharingProgram';
import TrueImpactCollapse from 'shared/design-system/components/TrueImpactCollapse';
import ReportSharingOptions, {
  getOrganizationVisibilityToSharing,
  getReportSharingFromReportVisibility,
  SharingType,
} from './ReportSharingOptions';
import { ReportVisibilityText } from './ReportSharingModal';
import {
  AccountVisibility,
  ReportVisibility,
  useUserActions,
} from 'shared/auth/hooks/useUserActions';
import { getMyAccount } from 'shared/apollo/provider';
import { DatabaseUserType } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';

interface MyAccount {
  email: string;
  firstName: string;
  id: string;
  lastName: string;
  logoFile: string;
  logoFileUrl: string;
  name: string;
  newProgramVisibility: ReportVisibility;
  type: DatabaseUserType;
  userId: number;
  userLogo: string;
  userType: DatabaseUserType;
  visibility: AccountVisibility;
}

const StyledDiv = styled.div`
  .header-container {
    background-color: ${COLORS.grayLight};
    padding: ${SPACERS.large};
  }
  .content-container {
    max-width: 650px;
    margin: ${SPACERS.extraLarge} auto;
    .selection-container {
      margin-top: ${SPACERS.extraLarge};
      margin-bottom: ${SPACERS.extraLarge};
      .selection-container-text {
        margin-bottom: ${SPACERS.medium};
      }
    }
    .programs-container {
      .programs-container-text {
        margin-bottom: ${SPACERS.medium};
      }
    }
  }
`;

const ReportSharing: React.FC = () => {
  const client = useClient();
  const { visibility, newProgramVisibility } = useUserActions();
  const [sharing, setSharing] = useState<SharingType>(
    getOrganizationVisibilityToSharing(visibility),
  );
  const [subSharing, setSubSharing] = useState<SharingType>(
    getReportSharingFromReportVisibility(newProgramVisibility),
  );
  const [requestAccountState, makeAccountRequest] = useGraphQLRequest<{
    myAccount: MyAccount[];
  }>();
  const [requestState, makeRequest, setRequestState] = useGraphQLRequest<
    AllReportYears
  >();

  useEffect(() => {
    if (!client) return;
    makeAccountRequest(getMyAccount(client));
    makeRequest(getAllReports(client));
  }, [client, makeAccountRequest, makeRequest]);

  useEffect(() => {
    if (requestAccountState.type === 'REQUEST_SUCCESS') {
      const myAccount = requestAccountState.data.myAccount[0];
      setSharing(getOrganizationVisibilityToSharing(myAccount.visibility));
      setSubSharing(
        getReportSharingFromReportVisibility(myAccount.newProgramVisibility),
      );
    }
  }, [requestAccountState]);

  const reports = useMemo(() => {
    if (requestState.type === 'REQUEST_SUCCESS') {
      return requestState.data;
    }
    return undefined;
  }, [requestState]);

  const handleProgramEnabled = useCallback(
    (programId: number, visibility: ReportVisibility) => {
      setRequestState(s => {
        if (s.type !== 'REQUEST_SUCCESS') return s;
        const newState = { ...s };
        newState.data[programId].reports.forEach(r => {
          if (visibility === 'PUBLIC' && r.level1DonorName) return;
          r.visibility = visibility;
        });
        return newState;
      });
    },
    [setRequestState],
  );

  if (
    !reports ||
    requestState.type === 'REQUEST_INIT' ||
    requestState.type === 'REQUEST_START' ||
    requestAccountState.type === 'REQUEST_INIT' ||
    requestAccountState.type === 'REQUEST_START'
  ) {
    return <TrueImpactLoading />;
  }

  return (
    <StyledDiv>
      <div className="header-container">
        <TrueImpactText variant="heading-page" className="title-heading">
          Reports Visibility
        </TrueImpactText>
      </div>
      <div className="content-container">
        <div className="selection-container">
          <ReportVisibilityText className="selection-container-text" />
          <ReportSharingOptions
            shouldSave={true}
            isLoginModal={false}
            selection={sharing}
            setSelection={setSharing}
            subSelection={subSharing}
            setSubSelection={setSubSharing}
          />
        </div>
        <TrueImpactCollapse
          className="programs-container"
          show={sharing === 'custom'}
          bufferHeight={1}
        >
          <div>
            <TrueImpactText
              className="programs-container-text"
              variant="heading-subheading"
            >
              You can now choose the visibility for each program or report
              individually.
            </TrueImpactText>
            {reports?.programs.map(p => (
              <ReportSharingProgram
                key={p.Id}
                program={p}
                reports={reports[p.Id]?.reports || []}
                handleProgramEnabled={handleProgramEnabled}
              />
            ))}
          </div>
        </TrueImpactCollapse>
      </div>
    </StyledDiv>
  );
};

export default ReportSharing;
